import React, { useState, useEffect, useCallback } from 'react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ScoreBadge = ({ score }) => {
  const cfg = score >= 70 ? 'bg-green-500' : score >= 50 ? 'bg-orange-500' : score >= 30 ? 'bg-yellow-500' : 'bg-gray-400';
  return <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-white text-xs font-bold ${cfg}`} data-testid="ai-lead-score">{score}</span>;
};

const ChatBubble = ({ msg }) => (
  <div className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}>
    <div className={`max-w-[80%] rounded-xl px-3 py-2 text-xs ${msg.role === 'user' ? 'bg-orange-100 text-orange-900' : 'bg-gray-100 text-gray-800'}`}>{msg.content}</div>
  </div>
);

// Aayush chatbot qualified leads — view, read conversation, convert into CRM.
export default function AiLeadsView({ token }) {
  const [aiLeads, setAiLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [convertingId, setConvertingId] = useState(null);
  const [selected, setSelected] = useState(null);
  const [chat, setChat] = useState([]);
  const [chatLoading, setChatLoading] = useState(false);

  const fetchAiLeads = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/live-chat/qualified-leads?min_score=1&limit=100`);
      const d = await res.json();
      if (d.success) setAiLeads(d.leads || []);
    } catch (e) { console.error('ai leads', e); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchAiLeads(); }, [fetchAiLeads]);

  const fetchChat = async (sessionId) => {
    setChatLoading(true); setChat([]);
    try {
      const res = await fetch(`${API_URL}/api/live-chat/chat-history/${sessionId}`);
      const d = await res.json();
      if (d.success) setChat(d.messages || []);
    } catch (e) { console.error('chat', e); }
    finally { setChatLoading(false); }
  };

  const convertToLead = async (sessionId) => {
    setConvertingId(sessionId);
    try {
      const res = await fetch(`${API_URL}/api/live-chat/convert-to-lead/${sessionId}`, { method: 'POST' });
      const d = await res.json();
      if (d.success) { toast.success(d.already_exists ? 'यह lead पहले से CRM में है' : 'Lead CRM में add हो गई! ✅'); fetchAiLeads(); }
      else toast.error('Convert नहीं हो पाया');
    } catch (e) { toast.error('Convert नहीं हो पाया'); }
    finally { setConvertingId(null); }
  };

  return (
    <div className="bg-white rounded-xl shadow-sm overflow-hidden" data-testid="crm-ai-leads-view">
      <div className="px-4 py-3 bg-gradient-to-r from-blue-50 to-indigo-50 border-b flex items-center justify-between">
        <div>
          <p className="font-bold text-slate-700 text-sm">🤖 Aayush Chatbot से आए Leads</p>
          <p className="text-[11px] text-slate-500">AI Score के अनुसार · "CRM +" से unified pipeline में जोड़ें</p>
        </div>
        <button onClick={fetchAiLeads} data-testid="crm-ai-leads-refresh" className="text-xs px-3 py-1.5 rounded-lg bg-blue-500 text-white hover:bg-blue-600">↻ Refresh</button>
      </div>
      {loading ? (
        <div className="py-12 text-center"><div className="inline-block animate-spin rounded-full h-6 w-6 border-b-2 border-blue-500" /></div>
      ) : aiLeads.length === 0 ? (
        <div className="p-10 text-center text-slate-400 text-sm" data-testid="crm-ai-leads-empty">अभी कोई AI lead नहीं</div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 border-b">
              <tr>{['Score', 'School', 'पद', 'छात्र', 'समस्या', 'Timeline', 'Actions'].map(h => (
                <th key={h} className="px-4 py-3 text-left text-[11px] font-semibold text-slate-500 uppercase tracking-wide">{h}</th>
              ))}</tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {aiLeads.map((lead, idx) => {
                const lp = lead.lead_profile || {};
                return (
                  <tr key={idx} className="hover:bg-slate-50" data-testid={`crm-ai-lead-row-${idx}`}>
                    <td className="px-4 py-3"><ScoreBadge score={lp.lead_score || 0} /></td>
                    <td className="px-4 py-3">
                      <p className="font-medium text-slate-800">{lp.school_name || '-'}</p>
                      {lead.lead_phone && <p className="text-xs text-slate-400">{lead.lead_phone}</p>}
                    </td>
                    <td className="px-4 py-3 text-slate-600">{lp.role || '-'}</td>
                    <td className="px-4 py-3 text-slate-600">{lp.student_count || '-'}</td>
                    <td className="px-4 py-3 text-xs text-slate-500 max-w-[200px] truncate">{lp.problems_described || (lp.has_problems === 'yes' ? 'Yes' : '-')}</td>
                    <td className="px-4 py-3 text-xs text-slate-500">{lp.decision_timeline || '-'}</td>
                    <td className="px-4 py-3">
                      <div className="flex gap-1.5">
                        <button onClick={() => { setSelected(lead); fetchChat(lead.session_id); }} data-testid={`crm-ai-view-${idx}`}
                          className="px-2 py-1 bg-blue-100 text-blue-700 rounded text-xs hover:bg-blue-200">देखें</button>
                        <button onClick={() => convertToLead(lead.session_id)} disabled={convertingId === lead.session_id || lead.converted_to_crm}
                          data-testid={`crm-ai-convert-${idx}`}
                          className={`px-2 py-1 rounded text-xs ${lead.converted_to_crm ? 'bg-green-100 text-green-700' : 'bg-orange-100 text-orange-700 hover:bg-orange-200'} disabled:opacity-50`}>
                          {lead.converted_to_crm ? 'CRM ✓' : convertingId === lead.session_id ? '...' : 'CRM +'}
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {selected && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" data-testid="crm-ai-lead-modal" onClick={() => { setSelected(null); setChat([]); }}>
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-2xl max-h-[85vh] overflow-hidden" onClick={(e) => e.stopPropagation()}>
            <div className="p-4 border-b bg-slate-50 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <h3 className="text-lg font-bold text-slate-800">{selected.lead_profile?.school_name || 'AI Lead'}</h3>
                <ScoreBadge score={selected.lead_profile?.lead_score || 0} />
              </div>
              <button onClick={() => { setSelected(null); setChat([]); }} className="text-slate-400 hover:text-slate-700 text-2xl">&times;</button>
            </div>
            <div className="p-4 overflow-y-auto max-h-[70vh] space-y-3">
              <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                {[
                  ['पद', selected.lead_profile?.role], ['Phone', selected.lead_phone], ['छात्र', selected.lead_profile?.student_count],
                  ['Software', selected.lead_profile?.uses_software], ['Timeline', selected.lead_profile?.decision_timeline],
                ].map(([l, v], i) => (
                  <div key={i} className="bg-slate-50 rounded-lg p-2.5"><p className="text-[10px] text-slate-400 uppercase">{l}</p><p className="text-sm font-medium text-slate-800 truncate">{v || '-'}</p></div>
                ))}
              </div>
              {selected.lead_profile?.problems_described && (
                <div className="bg-red-50 rounded-lg p-3 border border-red-100"><p className="text-[10px] text-red-500 uppercase mb-1">बताई गई समस्याएं</p><p className="text-sm text-red-800">{selected.lead_profile.problems_described}</p></div>
              )}
              <div className="bg-slate-50 rounded-lg p-3 border" data-testid="crm-ai-chat-history">
                <p className="text-[10px] text-slate-500 uppercase mb-2 font-medium">Aayush Conversation</p>
                {chatLoading ? (
                  <div className="flex justify-center py-4"><div className="animate-spin rounded-full h-5 w-5 border-b-2 border-blue-500" /></div>
                ) : chat.length === 0 ? (
                  <p className="text-xs text-slate-400 text-center py-2">कोई conversation नहीं</p>
                ) : (
                  <div className="space-y-2 max-h-60 overflow-y-auto">{chat.map((m, i) => <ChatBubble key={i} msg={m} />)}</div>
                )}
              </div>
              <div className="flex gap-3 pt-2 border-t">
                {selected.lead_phone && (
                  <>
                    <a href={`tel:+91${selected.lead_phone}`} className="flex-1 px-3 py-2 bg-blue-500 text-white rounded-lg text-center text-sm hover:bg-blue-600">📞 Call</a>
                    <a href={`https://wa.me/91${(selected.lead_phone || '').replace(/[^0-9]/g, '')}`} target="_blank" rel="noopener noreferrer" className="flex-1 px-3 py-2 bg-green-500 text-white rounded-lg text-center text-sm hover:bg-green-600">💬 WhatsApp</a>
                  </>
                )}
                {!selected.converted_to_crm && (
                  <button onClick={() => convertToLead(selected.session_id)} disabled={convertingId === selected.session_id}
                    data-testid="crm-ai-convert-modal" className="flex-1 px-3 py-2 bg-orange-500 text-white rounded-lg text-sm hover:bg-orange-600 disabled:opacity-50">
                    {convertingId === selected.session_id ? '...' : 'CRM में जोड़ें'}
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
